import type { Signal} from '@angular/core';
import { Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SwUpdate } from '@angular/service-worker';
import { filter } from 'rxjs';

/**
 * Detección de versiones nuevas de la PWA (patrón SwUpdate).
 *
 * El SW se registra solo en prod (app.config.ts) y sirve la app cache-first:
 * tras un deploy, la primera carga sigue mostrando la versión VIEJA hasta el
 * próximo full reload. Este servicio escucha `versionUpdates` (VERSION_READY =
 * la versión nueva ya está descargada y lista) y expone `updateReady` para que
 * el shell muestre el aviso "Hay una versión nueva — Actualizar".
 *
 * En dev (SW deshabilitado) es un no-op: `isEnabled` corta antes de suscribir.
 */
@Injectable({ providedIn: 'root' })
export class SwUpdateService {
  private readonly swUpdate = inject(SwUpdate);

  private readonly _updateReady = signal(false);
  /** True cuando hay una versión nueva descargada, lista para activar. */
  readonly updateReady: Signal<boolean> = this._updateReady.asReadonly();

  private readonly _activating = signal(false);
  /** True mientras se activa la versión nueva (feedback del botón Actualizar). */
  readonly activating: Signal<boolean> = this._activating.asReadonly();

  constructor() {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.swUpdate.versionUpdates
      .pipe(
        filter((event) => event.type === 'VERSION_READY'),
        takeUntilDestroyed(),
      )
      .subscribe(() => this._updateReady.set(true));
  }

  /**
   * Activa la versión descargada y recarga la página (el SW nuevo pasa a servir
   * los assets nuevos). El reload ocurre AUNQUE `activateUpdate` falle: un full
   * reload re-consulta al SW y deja a la app en el estado más fresco posible.
   */
  async applyUpdate(): Promise<void> {
    if (this._activating()) {
      return;
    }
    this._activating.set(true);
    try {
      await this.swUpdate.activateUpdate();
    } catch (error) {
      console.error('[SwUpdateService] activateUpdate falló, se recarga igual', error);
    }
    document.location.reload();
  }

  /** Oculta el aviso hasta el próximo VERSION_READY ("más tarde"). */
  dismiss(): void {
    this._updateReady.set(false);
  }
}
